import {
  useMediaQuery,
  useTheme,
  ListItemIcon,
  MenuItem,
  Typography,
  ListItem,
  ListItemText,
  Stack,
} from '@mui/material';
import {
  CombineUserEntityAccountsRequestBody,
  combineUserEntityAccounts,
  sendResetPassword,
} from 'api';
import DuplicateAccountDialog from './DuplicateAccountDialog';
import MobilePanelList from 'components/lists/MobilePanelList';
import { TAB_LAYOUT_HEADER_HEIGHT } from 'components/layout/FormTabNavigation';
import { AppContext } from 'context/appContext';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  useFetcher,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useRouteLoaderData,
  useRevalidator,
} from 'react-router';
import { ROUTES, routeWithParams } from 'router';
import {
  EntityTypes,
  FormMode,
  DisplayMode,
  Property,
  PropertyGroup,
  Role,
  ROLES,
  Status,
  TOWPAY_ROLE_IDS,
  TOWPAY_ROLE_NAMES,
  Tenant,
  TraxeroActionResponse,
  User,
  UserListItem,
} from 'types';
import {
  capitalize,
  getFormattedUserName,
  getBlankUser,
  logError,
} from 'utils';
import AuthService from 'utils/AuthService';
import ResendEmailDialog from './ResendRegistrationEmailDialog';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  MRT_SortingState,
  MRT_RowVirtualizer,
  MRT_Row,
} from 'material-react-table';
import {
  Add,
  Delete,
  Email,
  History,
  LockReset,
  Send,
  Warning,
} from '@mui/icons-material';
import EntityLookup from 'components/autocompletes/EntityLookup';
import {
  TableType,
  useDefaultTableOptions,
} from 'hooks/useDefaultTableOptions';
import MRTableToolbar from 'components/tables/PanelTableToolbar';
import { ListItemMenuButton } from 'components/buttons/ListItemMenuButton';
import FormFrame from 'components/layout/FormFrame';
import {
  getActionRouteForDisplayMode,
  getLoaderRouteForDisplayMode,
  PeopleTabActionHandler,
} from './PeopleTabLayout';
import {
  PeopleTabLayoutContext,
  PeopleTabLayoutLoaderData,
} from './PeopleTabLayout';
dayjs.extend(relativeTime);

// The Towpay roles are Autura staff roles, shown only to super admins.
const canSeeTowpayRoles = (): boolean =>
  AuthService.IsSuperAdmin() || AuthService.IsTheOne();

const visibleRoles = <T extends Role>(roles: Array<T>): Array<T> =>
  canSeeTowpayRoles()
    ? roles
    : roles.filter((r) => !TOWPAY_ROLE_IDS.includes(r.id));

// Tenant-mode list rows carry roles as a server-joined string ("Admin, Driver").
const visibleRoleNames = (roles: string): string =>
  canSeeTowpayRoles()
    ? roles
    : roles
        .split(', ')
        .filter((name) => !TOWPAY_ROLE_NAMES.includes(name))
        .join(', ');

export interface UsersRouteParams {
  params: {
    tenantID?: string | undefined;
    groupID?: string | undefined;
    propertyID?: string | undefined;
    userID?: string | undefined;
  };
}

export interface UserContext {
  user: User;
  setUser: (user: User) => void;
  formMode: FormMode;
  displayMode: DisplayMode;
  onBack: () => void;
  entity: Property | PropertyGroup | Tenant;
  fetcherKey: string;
}

export const UsersPanel = (): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const displayToast = useContext(ToastContext);
  const { breakpoints } = useTheme();
  const mobile = useMediaQuery(breakpoints.down('lg'));
  const { userID } = useParams() as UsersRouteParams['params'];
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { pathname, state: navState } = useLocation();
  const { displayMode, entity, fetcherKey } =
    useOutletContext<PeopleTabLayoutContext>();
  const fetcher = useFetcher({ key: fetcherKey });
  const { _users } = useRouteLoaderData(
    getLoaderRouteForDisplayMode(displayMode),
  ) as PeopleTabLayoutLoaderData;
  const [users, setUsers] = useState<(User | UserListItem)[]>([]);
  const [user, setUser] = useState<User>(
    (navState?.entity as User) || getBlankUser(state.tenant.id),
  );
  const [email, setEmail] = useState<string>('');
  const [roles, setRoles] = useState<Role[]>();
  const [showDialog, setShowDialog] = useState<'resend' | 'combine' | null>(
    null,
  );
  const canEditUser =
    AuthService.IsSuperAdmin() || state.entityPermissions.user.update;
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);
  const isSubmitting = fetcher.state === 'submitting';

  const canAddRemove = useMemo(() => {
    if (AuthService.IsSuperAdmin()) {
      return true;
    }
    if (displayMode === 'property') {
      return state.entityPermissions[EntityTypes.Property].update;
    }
    if (displayMode === 'group') {
      return state.entityPermissions[EntityTypes.PropertyGroup].update;
    }
    if (displayMode === 'tenant') {
      return state.entityPermissions[EntityTypes.User].update;
    }
    return false;
  }, [displayMode]);

  const disableRemoveActionForRow = useCallback((row: User | UserListItem) => {
    if (displayMode === 'tenant') {
      return true;
    }
    if (displayMode === 'property') {
      const property = row as User;
      if (property?.ancestry?.length) {
        return true;
      }
    }
  }, [displayMode]);

  useEffect(() => {
    const awaitLoaderData = async () => {
      const u = await _users;
      setUsers(u);
      setIsLoading(false);
    };
    awaitLoaderData();
  }, [_users]);

  useEffect(() => {
    //scroll to the top of the table when the sorting changes
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
    } catch (error) {
      logError(error as Error);
    }
  }, [sorting]);

  // handle action responses
  const getSuccessMessage = useCallback(
    (res: TraxeroActionResponse<PeopleTabActionHandler>): string => {
      switch (res.handler) {
        case PeopleTabActionHandler.TENANT_USER_CREATE:
        case PeopleTabActionHandler.PROPERTY_USER_CREATE:
        case PeopleTabActionHandler.PROPERTY_GROUP_USER_CREATE:
          return t('create_success', {
            name: `${t('user.label', {
              ns: 'entities',
            })} ${getFormattedUserName(user)}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.TENANT_USER_UPDATE:
        case PeopleTabActionHandler.PROPERTY_USER_UPDATE:
        case PeopleTabActionHandler.PROPERTY_GROUP_USER_UPDATE:
          return t('update_success', {
            name: `${t('user.label', { ns: 'entities' })}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.PROPERTY_USER_UPSERT:
          return t('update_success', {
            name: `${t('property.label', { ns: 'entities' })}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.PROPERTY_GROUP_USER_UPSERT:
          return t('update_success', {
            name: `${t('property_group.label', { ns: 'entities' })}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.USER_DELETE:
          return t('delete_success', {
            name: `${t('user.label', {
              ns: 'entities',
            })} ${getFormattedUserName(user)}`,
            ns: 'toasts',
          });
        default:
          return '';
      }
    },
    [],
  );
  const getErrorMessage = useCallback(
    (res: TraxeroActionResponse<PeopleTabActionHandler>): string => {
      const { handler, status, statusText } = res;
      switch (handler) {
        case PeopleTabActionHandler.TENANT_USER_UPDATE:
        case PeopleTabActionHandler.PROPERTY_USER_UPDATE:
        case PeopleTabActionHandler.PROPERTY_GROUP_USER_UPDATE:
          return t('update_error', {
            name: `${t('user.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.TENANT_USER_CREATE:
        case PeopleTabActionHandler.PROPERTY_USER_CREATE:
        case PeopleTabActionHandler.PROPERTY_GROUP_USER_CREATE:
          // Show specific message for user already exists error
          if (statusText.toLowerCase().includes('user already exists')) {
            return t('user_already_exists', { ns: 'toasts' });
          }
          return t('create_error', {
            name: `${t('user.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.PROPERTY_USER_UPSERT:
          return t('update_error', {
            name: `${t('property.label', { ns: 'entities' })} ${t(
              'user.label_plural',
              { ns: 'entities' },
            )}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.PROPERTY_GROUP_USER_UPSERT:
          return t('update_error', {
            name: `${t('property_group.label', { ns: 'entities' })} ${t(
              'user.label_plural',
              { ns: 'entities' },
            )}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case PeopleTabActionHandler.USER_DELETE:
          return t('delete_error', {
            name: `${t('user.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        default:
          return t('unknown_error', { ns: 'toasts' });
      }
    },
    [],
  );

  useEffect(() => {
    if (fetcher.data) {
      const response =
        fetcher.data as TraxeroActionResponse<PeopleTabActionHandler>;
      if (response.response?.activeAccount === true) {
        setShowDialog('combine');
        setEmail(user.email || '');
        setRoles(user.roles);
      } else if (response.status < 400) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: getSuccessMessage(response),
        });
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: getErrorMessage(response),
        });
      }
    }
  }, [fetcher]);

  const handleRemoveUser = (id: number) => {
    const updatedUsers = users.filter((u: User | UserListItem) => {
      if ((u as User)?.ancestry && Array.isArray((u as User).ancestry)) {
        const propertyUser = u as User;
        if (propertyUser?.ancestry?.length) {
          return false;
        }
      }
      return (u as UserListItem).id !== id;
    });
    
    fetcher.submit(
      {
        handler: getHandlerForDisplayMode(displayMode, 'none'),
        body: JSON.stringify(updatedUsers),
        userID: user.id,
        tenantID: state.tenant.id,
        entityID: entity.id,
      },
      {
        method: 'PUT',
        action: getActionRouteForDisplayMode({
          displayMode,
          propertyID: entity.id,
          groupID: entity.id,
          tenantID: state.tenant.id,
        }),
        encType: 'application/json',
      },
    );
  };
  const handleSoftDelete = (user: User | UserListItem): void => {
    if (!AuthService.IsSuperAdmin() && !AuthService.IsAdmin()) return;
    const updatedUser = { ...user, status: Status.Disabled };
    fetcher.submit(
      {
        handler: PeopleTabActionHandler.USER_DELETE,
        body: JSON.stringify(updatedUser),
        userID: user.id,
        tenantID: state.tenant.id,
        entityID: entity.id,
      },
      {
        method: 'PUT',
        action: getActionRouteForDisplayMode({
          displayMode,
          propertyID: entity.id,
          groupID: entity.id,
          tenantID: state.tenant.id,
        }),
        encType: 'application/json',
      },
    );
  };
  const handleEditUser = (id: number) => {
    if (!canEditUser) return;
    if (displayMode === 'tenant') {
      navigate(routeWithParams(ROUTES.USERS_FORM, [state.tenant.id, id]));
    } else {
      navigate(
        routeWithParams(getFormRouteForDisplayMode(displayMode), [
          state.tenant.id,
          entity?.id,
          id.toString(),
        ]),
      );
    }
  };
  const handleViewRevisions = (id: number) => {
    navigate(
      routeWithParams(ROUTES.REVISIONS, [
        state.tenant.id,
        EntityTypes.User,
        id.toString(),
      ]),
    );
  };
  const handleAddUser = (u: User | null) => {
    if (u) {
      const updatedUsers = [...users, u];
      fetcher.submit(
        {
          handler: getHandlerForDisplayMode(displayMode, 'none'),
          body: JSON.stringify(updatedUsers),
          tenantID: state.tenant.id,
          entityID: entity.id,
        },
        {
          method: 'PUT',
          action: getActionRouteForDisplayMode({
            displayMode,
            propertyID: entity.id,
            groupID: entity.id,
            tenantID: state.tenant.id,
          }),

          encType: 'application/json',
        },
      );
    }
  };
  const handleResendRegistration = async (item: User | UserListItem): Promise<void> => {
    setUser(item as User);
    setShowDialog('resend');
  };
  const handleResetPassword = async (user: User | UserListItem): Promise<void> => {
    if (!user.email) {
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t('send_error', {
          name: `${t('user_settings.resetPassword')} ${t('email')}`,
          error: 'User email is not set',
          ns: 'toasts',
        }),
      });
      return;
    }
    const res = await sendResetPassword(user.email);
    if (!res.ok) {
      const error = await res.text();
      logError(error as unknown as string);
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t('send_error', {
          name: `${t('user_settings.resetPassword')} ${t('email')}`,
          error: res.status,
          ns: 'toasts',
        }),
      });
    } else {
      displayToast({
        type: TOAST_TYPE.SUCCESS,
        message: t('reset_password_sent', { email: user?.email, ns: 'toasts' }),
      });
    }
  };

  const handleCloseDialog = useCallback(() => {
    setUser(getBlankUser(state.tenant.id));
    setShowDialog(null);
  }, []);
  const handleCreateClick = () => {
    navigate(`${pathname}/0`);
  };
  const handleCombineAccounts = async () => {
    const combineUserEntityBody: CombineUserEntityAccountsRequestBody = {
      email: email,
      roles: roles as Role[],
      entityType: EntityTypes.Tenant,
      entityID: state.tenant.id,
    };
    const res = await combineUserEntityAccounts(combineUserEntityBody);
    if (res.status === 200) {
      displayToast({
        type: TOAST_TYPE.SUCCESS,
        message: t('combine_accounts_success', { ns: 'toasts' }),
      });
      handleCloseDialog();
      revalidator.revalidate();
    } else {
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t('combine_accounts_error', {
          ns: 'toasts',
          error: res.statusText,
        }),
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: MRT_ColumnDef<any>[] = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cols: MRT_ColumnDef<any>[] = [
      {
        header: t('user.id', { ns: 'entities' }),
        accessorKey: 'id',
      },
      {
        header: t('user.externalID', { ns: 'entities' }),
        accessorKey: 'externalID',
      },
      {
        header: t('user.firstName', { ns: 'entities' }),
        accessorKey: 'firstName',
      },
      {
        header: t('user.lastName', { ns: 'entities' }),
        accessorKey: 'lastName',
      },
      {
        header: t('user.email', { ns: 'entities' }),
        accessorFn: (row) => {
          // UserListItem has email pre-resolved server-side (tenant mode)
          // User has contact fallback for property/group modes
          if (typeof row.roles === 'string' || !row.roles) {
            return row.email || '';
          }
          return (
            row.email ||
            row.contact?.primaryEmail ||
            row.contact?.secondaryEmail ||
            ''
          );
        },
      },
      {
        header: t('user.roles', { ns: 'entities' }),
        accessorKey: 'roles',
        accessorFn: (row) => {
          // UserListItem has roles as pre-joined string (tenant mode)
          if (typeof row.roles === 'string') {
            return visibleRoleNames(row.roles);
          }
          // User has roles as Role[] (property/group modes)
          if (row?.roles) {
            return visibleRoles(row.roles)
              .filter((r: Role) => r.tenantID === state.tenant.id)
              .map((r: Role) => r.name)
              .join(', ');
          }
          return '';
        },
        filterVariant: 'multi-select',
        filterSelectOptions: visibleRoles(ROLES).map((r) => ({
          label: r.name,
          value: r.name,
        })),
        filterFn: (row, id, filterValues: string[]) => {
          if (!filterValues.length) return true;
          const rolesStr = row.getValue<string>(id);
          return filterValues.some((filter) => rolesStr.includes(filter));
        },
      },
      {
        header: t('user.status', { ns: 'entities' }),
        accessorKey: 'status',
        filterVariant: 'select',
        filterSelectOptions: [
          {
            label: t('status.active', { ns: 'enums' }),
            value: Status.Active,
          },
          {
            label: t('status.disabled', { ns: 'enums' }),
            value: Status.Disabled,
          },
          {
            label: t('status.pending', { ns: 'enums' }),
            value: Status.Pending,
          },
          {
            label: t('status.external', { ns: 'enums' }),
            value: Status.External,
          },
        ],
        id: 'status',
        Cell: ({ row }: { row: MRT_Row<User | UserListItem> }) => {
          return capitalize((row.original as User).status);
        },
      },
      {
        header: t('user.created', { ns: 'entities' }),
        id: 'created',
        accessorFn: (row) => {
          if (row?.created) return new Date(row.created);
          return null;
        },
        Cell: ({ cell }) => dayjs(cell.getValue<Date>()).format('MM/DD/YYYY'),
        filterVariant: 'date',
        filterFn: (row, id, filterValue) => {
          return dayjs(row.getValue(id)).isSame(dayjs(filterValue), 'day');
        },
      },
      {
        header: t('user.updated', { ns: 'entities' }),
        id: 'updated',
        accessorFn: (row) => {
          if (row?.updated) return new Date(row.updated);
          return null;
        },
        Cell: ({ cell }) => dayjs(cell.getValue<Date>()).format('MM/DD/YYYY'),
        filterVariant: 'date',
        filterFn: (row, id, filterValue) => {
          return dayjs(row.getValue(id)).isSame(dayjs(filterValue), 'day');
        },
      },
    ];
    if (displayMode === 'property') {
      cols.push({
        header: t('from', {
          name: t('property_group.label', { ns: 'entities' }),
        }),
        id: 'ancestry',
        accessorFn: (row) => {
          const ancestry = (row as User).ancestry;
          if (!ancestry?.length) return '';
          return ancestry
            .map((a) => a.propertyGroupName)
            .filter(Boolean)
            .join(', ');
        },
      });
    }
    return cols;
  }, [displayMode]);

  const getRolesDisplay = (item: User | UserListItem): string => {
    if (typeof item.roles === 'string') return visibleRoleNames(item.roles);
    if (Array.isArray(item.roles)) {
      return visibleRoles(item.roles)
        .map((r: Role) => capitalize(r.name))
        .join(', ');
    }
    return '';
  };

  const itemHeight = 70;
  const renderItem = (item: User | UserListItem): ReactNode => (
    <ListItem
      key={item.id}
      id={item.id.toString()}
      onClick={() => handleEditUser(item.id)}
      divider
      secondaryAction={
        <ListItemMenuButton
          menuOptions={[
            {
              content: t('send_password_reset_email'),
              onClick: () => handleResetPassword(item),
              disabled: item.status !== Status.Active,
              icon: <LockReset />,
            },
            {
              content: item.status === Status.External ? t('labels.resend_registration_external', { ns: 'userSettings' }) : t('labels.resend_registration', { ns: 'userSettings' }),
              onClick: () => handleResendRegistration(item),
              disabled: item.status !== Status.Pending,
              icon: <Email />,
            },
          ]}
        />
      }
      sx={{ height: itemHeight, backgroundColor: 'white' }}
    >
      <ListItemText
        primary={<Typography variant={'subtitle1'} noWrap>{getFormattedUserName(item)}</Typography>}
        secondary={<Typography variant={'subtitle2'} noWrap>{getRolesDisplay(item)}</Typography>}
      />
    </ListItem>
  );

  const tableDefaults = useDefaultTableOptions<User | UserListItem>(TableType.NESTED);
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: users,
    columns,
    enableRowVirtualization: true,
    rowVirtualizerInstanceRef,
    rowVirtualizerOptions: { overscan: 5 },
    enableRowSelection: false,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      density: 'compact',
      columnVisibility: {
        id: AuthService.IsSuperAdmin(),
        externalID: AuthService.IsSuperAdmin(),
        firstName: true,
        lastName: true,
        email: true,
        roles: true,
        status: true,
        ancestry: false,
        created: false,
        updated: false,
        pinned: false,
      },
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    state: {
      isLoading,
      sorting,
      showLoadingOverlay: false,
    },
    onSortingChange: setSorting,
    getRowId: (row) => {
      return `${row.id}`;
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: 'white',
        cursor: 'pointer',
      },
      onClick: () => handleEditUser(row.original.id),
    }),
    renderTopToolbar: ({ table }) => (
      <MRTableToolbar
        table={table}
        hidden={!!userID}
        optionsDisabled={!canAddRemove}
        isSubmitting={isSubmitting}
        tableType={TableType.NESTED}
        menuOptions={[
          {
            onClick: () => handleCreateClick(),
            content: t('buttons.create', {
              name: t('user.label', { ns: 'entities' }),
            }),
            disabled: !canAddRemove,
            icon: <Add />,
          },
        ]}
      >
        <Stack
          direction={'row'}
          flexGrow={1}
          justifyContent={
            displayMode === 'tenant' ? 'flex-start' : 'space-between'
          }
          alignItems={'center'}
        >
          <Typography variant={'h3'} sx={{ flex: 1 }}>
            {t('user.label_plural', { ns: 'entities' })}
          </Typography>
          {displayMode !== 'tenant' && (
            <EntityLookup
              entityType={EntityTypes.User}
              onSelect={handleAddUser}
              renderOption={(option: User) => {
                return (
                  <React.Fragment key={option.id}>
                    <Typography variant={'body2'} noWrap fontWeight={'bold'}>
                      {`${option.firstName} ${option.lastName}`}
                    </Typography>
                    <Typography variant={'body2'} noWrap>
                      {`${
                        option?.email ||
                        t('labels.noEmail', { ns: 'userSettings' })
                      }`}
                    </Typography>
                  </React.Fragment>
                );
              }}
              size='small'
              sx={{ width: '30%' }}
            />
          )}
        </Stack>
      </MRTableToolbar>
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => {
      const options = [
        <MenuItem
          key={1}
          divider
          onClick={() => {
            handleRemoveUser(row.original.id);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
          disabled={disableRemoveActionForRow(row.original)}
        >
          <ListItemIcon>
            <Delete />
          </ListItemIcon>
          {t('buttons.remove', { name: t('user.label', { ns: 'entities' }) })}
        </MenuItem>,
        <MenuItem
          key={2}
          divider
          onClick={() => {
            handleViewRevisions(row.original.id);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
        >
          <ListItemIcon>
            <History />
          </ListItemIcon>
          {t('buttons.view', { name: t('revision.label_plural') })}
        </MenuItem>,
      ];
      if (
        row.original.status === Status.Pending ||
        row.original.status === Status.External
      ) {
        options.push(
          <MenuItem
            key={3}
            onClick={() => {
              handleResendRegistration(row.original);
              closeMenu();
            }}
            sx={{ m: 0, backgroundColor: 'white' }}
          >
            <ListItemIcon>
              <Send />
            </ListItemIcon>
            {row.original.status === Status.External
              ? t('labels.resend_registration_external', { ns: 'userSettings' })
              : t('labels.resend_registration', { ns: 'userSettings' })}
          </MenuItem>,
        );
      }
      if (row.original.status === Status.Active) {
        options.push(
          <MenuItem
            key={4}
            onClick={() => handleResetPassword(row.original)}
            sx={{ m: 0, backgroundColor: 'white' }}
          >
            <ListItemIcon>
              <LockReset />
            </ListItemIcon>
            {t('labels.reset_password', { ns: 'userSettings' })}
          </MenuItem>,
        );
      }
      if (AuthService.IsSuperAdmin() || AuthService.IsAdmin()) {
        options.push(
          <MenuItem
            key={5}
            onClick={() => {
              handleSoftDelete(row.original);
              closeMenu();
            }}
            sx={{
              m: 0,
              backgroundColor: 'warning.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
            }}
          >
            <ListItemIcon>
              <Warning sx={{ color: 'white' }} />
            </ListItemIcon>
            {t('buttons.delete', { name: t('user.label', { ns: 'entities' }) })}
          </MenuItem>,
        );
      }
      return options;
    },
  });

  return (
    <React.Fragment>
      {showDialog === 'resend' && (
        <ResendEmailDialog
          isOpen={showDialog === 'resend'}
          handleClose={handleCloseDialog}
          user={user}
        />
      )}
      {showDialog === 'combine' && (
        <DuplicateAccountDialog
          open={showDialog === 'combine'}
          email={email || ''}
          handleClose={handleCloseDialog}
          handleAction={handleCombineAccounts}
        />
      )}
      {mobile && (
        <MobilePanelList<User | UserListItem>
          data={users}
          columns={columns.map((c) => ({
            name: c.header,
            searchKey: c.accessorKey,
          }))}
          renderItem={renderItem}
          title={t('user.label_plural', { ns: 'entities' })}
          itemHeight={itemHeight}
          withSearchBar
          heightOffset={TAB_LAYOUT_HEADER_HEIGHT}
          menuOptions={[
            {
              onClick: () => handleCreateClick(),
              content: t('buttons.create', { name: t('user.label', { ns: 'entities' }) }),
              disabled: !canAddRemove,
              icon: <Add />,
            },
          ]}
        />
      )}
      {!mobile && (
        <FormFrame loading={false}>
          <MaterialReactTable table={table} />
        </FormFrame>
      )}
    </React.Fragment>
  );
};

export const getFormRouteForDisplayMode = (
  displayMode: DisplayMode,
): ROUTES => {
  switch (displayMode) {
    case 'property':
      return ROUTES.PROPERTY_USERS_FORM;
    case 'group':
      return ROUTES.PROPERTY_GROUP_USERS_FORM;
    case 'tenant':
      return ROUTES.USERS_FORM;
    default:
      return ROUTES.INDEX;
  }
};

export const getHandlerForDisplayMode = (
  displayMode: DisplayMode,
  formMode: FormMode,
): PeopleTabActionHandler => {
  switch (displayMode) {
    case 'property':
      if (formMode === 'create') {
        return PeopleTabActionHandler.PROPERTY_USER_CREATE;
      } else if (formMode === 'none') {
        return PeopleTabActionHandler.PROPERTY_USER_UPSERT;
      } else if (formMode === 'edit') {
        return PeopleTabActionHandler.PROPERTY_USER_UPDATE;
      } else {
        return PeopleTabActionHandler.UNKNOWN;
      }
    case 'group':
      if (formMode === 'create') {
        return PeopleTabActionHandler.PROPERTY_GROUP_USER_CREATE;
      } else if (formMode === 'none') {
        return PeopleTabActionHandler.PROPERTY_GROUP_USER_UPSERT;
      } else if (formMode === 'edit') {
        return PeopleTabActionHandler.PROPERTY_GROUP_USER_UPDATE;
      } else {
        return PeopleTabActionHandler.UNKNOWN;
      }
    case 'tenant':
      if (formMode === 'create') {
        return PeopleTabActionHandler.TENANT_USER_CREATE;
      } else if (formMode === 'edit') {
        return PeopleTabActionHandler.TENANT_USER_UPDATE;
      } else {
        return PeopleTabActionHandler.UNKNOWN;
      }
    default:
      return PeopleTabActionHandler.UNKNOWN;
  }
};

export * from './UserForm';
